import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Popover,
  Box,
  Typography,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  setselectedDate,
  setselectedProffesionalDetails,
  setselectedTime,
  setselectedTreatmentTypes,
} from "../../store/slice/Salon.slice";
import { format_Number } from "../../utils/utils";

const IMG_URL = process.env.REACT_APP_API_LOCAL_IMG_URL;

const TreatmentType = ({ Ref, isOpen, handleClose, loader }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [imgError, setImgError] = useState({});
  const { css } = useSelector((store) => store?.widgetSettings);
  const treatmentCss = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Treatment"
  )?.fields;

  const { treatmentType, selectedTreatmentType, languageWords } = useSelector(
    (store) => store?.salon
  );
  const dispatch = useDispatch();

  const [anchorOriginVert, setAnchorOriginVert] = useState("bottom");
  const [transformOriginVert, setTransformOriginVert] = useState("top");

  useEffect(() => {
    if (!isOpen || !Ref?.current) return;
    const rect = Ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < 400) {
      setAnchorOriginVert("top");
      setTransformOriginVert("bottom");
    } else {
      setAnchorOriginVert("bottom");
      setTransformOriginVert("top");
    }
  }, [isOpen, Ref]);

  // Responsive image dimensions
  const imgWidth  = isMobile ? 72 : isTablet ? 100 : 130;
  const imgHeight = isMobile ? 90 : isTablet ? 90  : 110;

  return (
    <Popover
      open={isOpen}
      anchorEl={Ref?.current}
      onClose={handleClose}
      anchorOrigin={{ vertical: anchorOriginVert, horizontal: "center" }}
      transformOrigin={{ vertical: transformOriginVert, horizontal: "center" }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.14)",
          overflow: "hidden",
          mt: 0.5,
          // On mobile: almost full width. On desktop: wide card list.
          width: isMobile
            ? "min(96vw, 420px)"
            : isTablet
            ? "min(90vw, 600px)"
            : "clamp(480px, 55vw, 860px)",
          maxHeight: { xs: "72vh", sm: "75vh", md: "620px" },
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
          backgroundColor: treatmentCss?.treatmentBgMainClr || "#fff",
        },
      }}
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
    >
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>

        {/* ── Sticky header ── */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: treatmentCss?.treatmentBgMainClr || "#fff",
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.25, sm: 1.5 },
            borderBottom: "1px solid",
            borderColor: "rgba(0,0,0,0.06)",
          }}
        >
          <Typography
            sx={{
              color: treatmentCss?.treatmentHeadingClr || "#333",
              fontSize: { xs: "0.8125rem", sm: "0.875rem" },
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
          >
            {languageWords?.["Treatment"] || "Treatment"}
          </Typography>
        </Box>

        {/* ── Content ── */}
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          {loader ? (
            /* ── Skeleton state ── */
            <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1, sm: 1.5 } }}>
              {Array.from({ length: 3 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: { xs: 1.25, sm: 2 },
                    borderRadius: "12px",
                    p: { xs: 1.25, sm: 1.5 },
                    backgroundColor: "#FAFAFA",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      borderRadius: "10px",
                      flexShrink: 0,
                      width: imgWidth,
                      height: imgHeight,
                    }}
                  />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Skeleton width="55%" height={18} sx={{ mb: 0.75 }} />
                    <Skeleton width="30%" height={14} sx={{ mb: 0.75 }} />
                    <Skeleton width="90%" height={12} />
                    <Skeleton width="75%" height={12} sx={{ mt: 0.5 }} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            /* ── Treatment list ── */
            <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1, sm: 1.5 } }}>
              {treatmentType
                ?.filter((item) => item?.active)
                ?.map((item) => {
                  const hasItemImage =
                    item?.imageFileName && !imgError[item?.idSalonTreatment];
                  const isSelected =
                    selectedTreatmentType?.idSalonTreatment ===
                    item?.idSalonTreatment;

                  return (
                    <Box
                      key={item?.idSalonTreatment}
                      onClick={() => {
                        dispatch(setselectedTreatmentTypes(item));
                        dispatch(setselectedProffesionalDetails({}));
                        dispatch(setselectedDate(""));
                        dispatch(setselectedTime(""));
                        handleClose();
                      }}
                      sx={{
                        display: "flex",
                        // Stack image on top on mobile, side-by-side on larger
                        flexDirection: { xs: "column", sm: "row" },
                        gap: { xs: 1, sm: 1.5 },
                        border: "1px solid",
                        borderColor: isSelected
                          ? treatmentCss?.treatmentBorderColor || "#A57939"
                          : treatmentCss?.treatmentBorderColor || "#e8e8e8",
                        backgroundColor: isSelected
                          ? treatmentCss?.treatmentItemSelectBgClr || "rgba(165,121,57,0.08)"
                          : treatmentCss?.treatmentItemBgClr || "#fff",
                        p: { xs: 1.25, sm: 1.5 },
                        borderRadius: "12px",
                        color: isSelected
                          ? treatmentCss?.treatmentItemSelectTxtClr || "#A57939"
                          : treatmentCss?.treatmentItemTxtClr || "#333",
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          transform: { xs: "none", sm: "scale(1.012)" },
                          backgroundColor:
                            treatmentCss?.treatmentBghover || "rgba(165,121,57,0.06)",
                          color: treatmentCss?.treatmentTxtHover || "#A57939",
                          boxShadow: {
                            xs: "none",
                            sm: "0px 4px 16px rgba(0,0,0,0.1)",
                          },
                        },
                        // Active press feedback on mobile
                        "&:active": { opacity: 0.85 },
                      }}
                    >
                      {/* ── Treatment image ── */}
                      {hasItemImage && (
                        <Box
                          sx={{
                            flexShrink: 0,
                            width: { xs: "100%", sm: `${imgWidth}px` },
                            height: { xs: "140px", sm: `${imgHeight}px` },
                            borderRadius: "10px",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={`${IMG_URL}upload/SalonTreatment/${item?.imageFileName.replace(
                              /\\/g, "/"
                            )}`}
                            alt={item?.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                            onError={() =>
                              setImgError((prev) => ({
                                ...prev,
                                [item?.idSalonTreatment]: true,
                              }))
                            }
                          />
                        </Box>
                      )}

                      {/* ── Text content ── */}
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        {/* Title + Price row */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                              fontWeight: 600,
                              flex: "1 1 0",
                              minWidth: 0,
                              wordBreak: "break-word",
                              lineHeight: 1.4,
                              color: isSelected
                                ? treatmentCss?.treatmentItemSelectTxtClr || "#A57939"
                                : treatmentCss?.treatmentItemTxtClr || "#222",
                            }}
                          >
                            {item?.name}
                          </Typography>

                          {item?.price > 0 && (
                            <Typography
                              sx={{
                                fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                                fontWeight: 700,
                                flexShrink: 0,
                                whiteSpace: "nowrap",
                                color: isSelected
                                  ? treatmentCss?.treatmentPriceSelectClr || "#A57939"
                                  : treatmentCss?.treatmmentPriceClr || "#555",
                              }}
                            >
                              €{format_Number(parseFloat(item?.price).toFixed(2))}
                            </Typography>
                          )}
                        </Box>

                        {/* Description */}
                        {item?.info && (
                          <Typography
                            sx={{
                              fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                              lineHeight: 1.55,
                              color: isSelected
                                ? treatmentCss?.treatmentInfoSelectClr || "#A57939"
                                : treatmentCss?.treatmentInfoClr || "#777",
                            }}
                          >
                            {item?.info}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          )}
        </Box>
      </Box>
    </Popover>
  );
};

export default TreatmentType;
